import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ApiRequestError,
  fetchJoinedTeamMembersPage,
  showApiResultModal,
} from '@/api';
import {
  RECURRING_INTERVALS,
  RECURRING_WEEKDAYS,
  fetchRecurringRules,
  postDeleteRecurringRule,
  formatRecurringServiceTime12,
  joinRecurringServiceTime,
  postUpdateRecurringRule,
  recurringIntervalLabel,
  recurringWeekdayLabel,
  splitRecurringServiceTime,
  type RecurringRule,
  type RecurringRuleStatus,
} from '@/api/modules/recurringOrders';
import { FormCheckbox } from '@/components/ui/FormCheckbox';
import { OptionSelect } from '@/components/ui/OptionSelect';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { RecurringServiceTimeFields } from '@/pages/orders/components/RecurringServiceTimeFields';

type StatusFilter = 'all' | 'active' | 'stopped';

type EditDraft = {
  intervalWeeks: number;
  dayOfWeek: number;
  serviceTime: string;
  totalTimes: string;
  operatorIds: string[];
  status: RecurringRuleStatus;
};

function clockValue(value: string): string {
  const { meridiem, clock } = splitRecurringServiceTime(value);
  return joinRecurringServiceTime(clock, meridiem);
}

function draftFromRule(rule: RecurringRule): EditDraft {
  return {
    intervalWeeks: rule.intervalWeeks > 0 ? rule.intervalWeeks : 1,
    dayOfWeek: rule.dayOfWeek >= 0 && rule.dayOfWeek <= 6 ? rule.dayOfWeek : 0,
    serviceTime: clockValue(rule.serviceTime),
    totalTimes: String(rule.totalTimes > 0 ? rule.totalTimes : 1),
    operatorIds: [...rule.operatorIds],
    status: rule.status,
  };
}

export default function RecurringOrdersPage() {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<StatusFilter>('all');
  const [rules, setRules] = useState<RecurringRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [editing, setEditing] = useState<RecurringRule | null>(null);
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [editError, setEditError] = useState('');
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<Array<{ id: string; name: string }>>([]);

  const loadRules = useCallback(async (nextFilter: StatusFilter) => {
    setLoading(true);
    setError('');
    try {
      const status = nextFilter === 'active' ? 1 : nextFilter === 'stopped' ? 0 : undefined;
      setRules(await fetchRecurringRules(status));
    } catch (e) {
      setRules([]);
      setError(e instanceof ApiRequestError ? e.message : 'Failed to load recurring rules.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadRules(filter);
  }, [filter, loadRules]);

  useEffect(() => {
    let cancelled = false;
    fetchJoinedTeamMembersPage({ page: 1, pageSize: 200 })
      .then((result) => {
        if (cancelled) return;
        setMembers(result.items.map((member) => ({
          id: String(member.operatorId),
          name: member.name,
        })));
      })
      .catch(() => {
        if (!cancelled) setMembers([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const openEdit = (rule: RecurringRule) => {
    setEditing(rule);
    setDraft(draftFromRule(rule));
    setEditError('');
  };

  const toggleStatus = async (rule: RecurringRule) => {
    if (busyId) return;
    setBusyId(rule.id);
    try {
      const nextStatus: RecurringRuleStatus = rule.status === 1 ? 0 : 1;
      const result = await postUpdateRecurringRule({ ruleId: rule.id, status: nextStatus });
      showApiResultModal({ message: result.message, success: true });
      await loadRules(filter);
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to update the rule.',
        success: false,
      });
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async (ruleId: string) => {
    if (busyId) return;
    setBusyId(ruleId);
    try {
      const result = await postDeleteRecurringRule(ruleId);
      setDeleteId(null);
      showApiResultModal({ message: result.message, success: true });
      await loadRules(filter);
    } catch (e) {
      showApiResultModal({
        message: e instanceof ApiRequestError ? e.message : 'Failed to delete the rule.',
        success: false,
      });
    } finally {
      setBusyId(null);
    }
  };

  const saveEdit = async () => {
    if (!editing || !draft || saving) return;
    const visits = Number(draft.totalTimes);
    if (!Number.isInteger(visits) || visits < 1) {
      setEditError('Enter the total number of visits.');
      return;
    }
    if (!/^\d{2}:\d{2}$/.test(draft.serviceTime)) {
      setEditError('Choose a service time.');
      return;
    }
    setSaving(true);
    setEditError('');
    try {
      const result = await postUpdateRecurringRule({
        ruleId: editing.id,
        intervalWeeks: draft.intervalWeeks,
        dayOfWeek: draft.dayOfWeek,
        serviceTime: draft.serviceTime,
        totalTimes: visits,
        operatorIds: draft.operatorIds,
        status: draft.status,
      });
      setEditing(null);
      setDraft(null);
      showApiResultModal({ message: result.message, success: true });
      await loadRules(filter);
    } catch (e) {
      setEditError(e instanceof ApiRequestError ? e.message : 'Failed to update the rule.');
    } finally {
      setSaving(false);
    }
  };

  const staffOptions = (rule: RecurringRule | null) => {
    const known = new Map(members.map((member) => [member.id, member.name]));
    rule?.operatorIds.forEach((id, index) => {
      if (!known.has(id)) known.set(id, rule.operatorNames[index] || `Staff #${id}`);
    });
    return [...known.entries()].map(([id, name]) => ({ id, name }));
  };

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Recurring Orders"
        subtitle="Auto-renewal rules for your team"
        onBack={() => navigate('/me')}
      />

      <div className="px-4 pt-3 pb-6">
        <div className="flex gap-2 mb-3">
          {([
            ['all', 'All'],
            ['active', 'Active'],
            ['stopped', 'Stopped'],
          ] as const).map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => setFilter(key)}
              className={`h-8 px-3 rounded-full text-xs font-semibold border ${
                filter === key
                  ? 'bg-green-600 border-green-600 text-white'
                  : 'bg-white border-gray-200 text-gray-600'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {error ? (
          <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>
        ) : null}

        {loading ? (
          <div className="text-center py-16">
            <i className="ri-loader-4-line text-3xl text-green-500 animate-spin mb-3 block" />
            <div className="text-sm text-gray-500">Loading rules…</div>
          </div>
        ) : rules.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-lg border border-gray-200">
            <i className="ri-repeat-line text-4xl text-gray-300 mb-3 block" />
            <div className="text-sm text-gray-400 font-medium">No recurring rules</div>
            <div className="text-xs text-gray-300 mt-1">Create one from an order in the Orders list.</div>
          </div>
        ) : (
          <div className="space-y-3">
            {rules.map((rule) => {
              const staffLabel = rule.operatorNames.length
                ? rule.operatorNames.join(', ')
                : rule.operatorIds.length
                  ? `${rule.operatorIds.length} staff`
                  : 'No default staff';
              const visits = rule.usedTimes !== undefined
                ? `${rule.usedTimes}/${rule.totalTimes || 0}`
                : String(rule.totalTimes || 0);
              return (
                <div key={rule.id} className="bg-white border border-gray-200 rounded-lg px-3.5 py-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-900 truncate">
                        {rule.customerName || (rule.orderId ? `Order #${rule.orderId}` : `Rule #${rule.id}`)}
                      </div>
                      <div className="text-[11px] text-gray-400 mt-0.5">
                        {rule.orderId ? `#${rule.orderId}` : `Rule #${rule.id}`}
                      </div>
                    </div>
                    <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border flex-shrink-0 ${
                      rule.status === 1
                        ? 'bg-green-50 text-green-700 border-green-200'
                        : 'bg-gray-50 text-gray-500 border-gray-200'
                    }`}
                    >
                      {rule.status === 1 ? 'Active' : 'Stopped'}
                    </span>
                  </div>
                  {rule.address ? (
                    <div className="text-[11px] text-gray-500 mt-2 truncate">{rule.address}</div>
                  ) : null}
                  <div className="mt-2 text-xs text-gray-700 space-y-1">
                    <div>{recurringIntervalLabel(rule.intervalWeeks)} · {recurringWeekdayLabel(rule.dayOfWeek)} {formatRecurringServiceTime12(rule.serviceTime)}</div>
                    <div>Visits {visits}</div>
                    <div className="text-gray-500 truncate">{staffLabel}</div>
                    {rule.nextServiceTime ? <div className="text-gray-500">Next {rule.nextServiceTime}</div> : null}
                  </div>
                  {deleteId === rule.id ? (
                    <div className="mt-3 rounded-lg bg-red-50 border border-red-100 px-3 py-2">
                      <div className="text-xs text-red-700 mb-2">Delete this recurring rule?</div>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => setDeleteId(null)}
                          className="flex-1 h-8 rounded-lg border border-gray-200 bg-white text-xs text-gray-600"
                        >
                          Cancel
                        </button>
                        <button
                          type="button"
                          disabled={busyId === rule.id}
                          onClick={() => void confirmDelete(rule.id)}
                          className="flex-1 h-8 rounded-lg bg-red-600 text-white text-xs font-semibold disabled:opacity-60"
                        >
                          {busyId === rule.id ? 'Deleting…' : 'Delete'}
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="mt-3 flex flex-wrap gap-1.5">
                      <button type="button" onClick={() => openEdit(rule)} className="h-8 px-2.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700">
                        Edit
                      </button>
                      <button
                        type="button"
                        disabled={busyId === rule.id}
                        onClick={() => void toggleStatus(rule)}
                        className="h-8 px-2.5 rounded-lg border border-gray-200 text-xs font-medium text-gray-700 disabled:opacity-60"
                      >
                        {rule.status === 1 ? 'Stop' : 'Resume'}
                      </button>
                      <button type="button" onClick={() => setDeleteId(rule.id)} className="h-8 px-2.5 rounded-lg border border-red-100 text-xs font-medium text-red-600">
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && draft ? (
        <div className="fixed inset-0 z-[80] flex items-end justify-center">
          <button
            type="button"
            className="absolute inset-0 bg-black/40"
            aria-label="Close"
            onClick={() => {
              if (!saving) {
                setEditing(null);
                setDraft(null);
              }
            }}
          />
          <div className="relative w-full max-w-[430px] bg-white rounded-t-2xl px-4 pt-4 pb-6 shadow-xl max-h-[88vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-base font-bold text-gray-900">Edit Rule</h2>
              <button
                type="button"
                onClick={() => {
                  if (!saving) {
                    setEditing(null);
                    setDraft(null);
                  }
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400"
                aria-label="Close"
              >
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            <label className="block text-xs font-semibold text-gray-600 mb-1">Repeat</label>
            <OptionSelect
              className="mb-3"
              value={String(draft.intervalWeeks)}
              options={(RECURRING_INTERVALS.some((item) => item.weeks === draft.intervalWeeks)
                ? RECURRING_INTERVALS
                : [...RECURRING_INTERVALS, { weeks: draft.intervalWeeks, label: recurringIntervalLabel(draft.intervalWeeks) }]
              ).map((item) => ({ value: String(item.weeks), label: item.label }))}
              onChange={(value) => setDraft({ ...draft, intervalWeeks: Number(value) })}
            />

            <label className="block text-xs font-semibold text-gray-600 mb-1">Weekday</label>
            <OptionSelect
              className="mb-3"
              value={String(draft.dayOfWeek)}
              options={RECURRING_WEEKDAYS.map((label, index) => ({ value: String(index), label }))}
              onChange={(value) => setDraft({ ...draft, dayOfWeek: Number(value) })}
            />

            <label className="block text-xs font-semibold text-gray-600 mb-1">Service time</label>
            <RecurringServiceTimeFields
              value={draft.serviceTime}
              onChange={(serviceTime) => setDraft({ ...draft, serviceTime })}
            />

            <label className="block text-xs font-semibold text-gray-600 mb-1">Total visits</label>
            <input
              type="number"
              min={1}
              step={1}
              value={draft.totalTimes}
              onChange={(e) => setDraft({ ...draft, totalTimes: e.target.value })}
              className="w-full h-10 mb-3 rounded-lg border border-gray-200 px-3 text-sm"
            />

            <label className="block text-xs font-semibold text-gray-600 mb-1">Status</label>
            <OptionSelect
              className="mb-3"
              value={String(draft.status)}
              options={[
                { value: '1', label: 'Active' },
                { value: '0', label: 'Stopped' },
              ]}
              onChange={(value) => setDraft({ ...draft, status: value === '0' ? 0 : 1 })}
            />

            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-600 mb-2">Default staff</div>
              {staffOptions(editing).length === 0 ? (
                <div className="text-xs text-gray-400">No team members found.</div>
              ) : (
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {staffOptions(editing).map((person) => (
                    <FormCheckbox
                      key={person.id}
                      checked={draft.operatorIds.includes(person.id)}
                      label={person.name}
                      onChange={() => {
                        setDraft({
                          ...draft,
                          operatorIds: draft.operatorIds.includes(person.id)
                            ? draft.operatorIds.filter((id) => id !== person.id)
                            : [...draft.operatorIds, person.id],
                        });
                      }}
                    />
                  ))}
                </div>
              )}
            </div>

            {editError ? (
              <div className="mb-3 text-xs text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{editError}</div>
            ) : null}

            <button
              type="button"
              disabled={saving}
              onClick={() => void saveEdit()}
              className="w-full h-11 rounded-xl bg-green-600 text-white text-sm font-semibold disabled:opacity-60"
            >
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
